import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Plus, Printer, Edit3, Save, X, Archive } from 'lucide-react';
import { formatDateFR, formatDateShortFR, todayZurich } from '../utils/dateFormat';
import { STATIONS } from '../utils/kitchenTasks';
import TaskFormDialog from '../components/tasks/TaskFormDialog';
import TaskTrackingPanel from '../components/tasks/TaskTrackingPanel';
import SelectionBar from '../components/tasks/SelectionBar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

// Extracted sub-components
import { TileGrid, BackButton, SectionHeader } from '../components/common/TileGrid';
import AdminPrepItemRow from '../components/prep/AdminPrepItemRow';
import MemberPrepItemRow from '../components/prep/MemberPrepItemRow';
import PrintPrepDialog from '../components/prep/PrintPrepDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GererPage = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission('create_prep_list'); // vue « chef » (cocher + Enregistrer) pilotée par la matrice
  const canManageItems = hasPermission('manage_prep_items');

  const today = todayZurich();
  const [selectedDate, setSelectedDate] = useState(today);
  const [savedDates, setSavedDates] = useState([]);
  const [showSavedDates, setShowSavedDates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState('all');

  // ===== ADMIN STATE =====
  const [allItems, setAllItems] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [dailyPrep, setDailyPrep] = useState([]);
  const [localPrep, setLocalPrep] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemStation, setNewItemStation] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printStation, setPrintStation] = useState('all');

  // ===== KITCHEN TASKS STATE =====
  const [activeTab, setActiveTab] = useState('liste');
  const [collaborators, setCollaborators] = useState([]);
  const [taskDialog, setTaskDialog] = useState(null); // null | 'bulk' | 'free'
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);
  const [savedTaskCount, setSavedTaskCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/kitchen-tasks`, { params: { date: selectedDate } })
      .then(res => { if (!cancelled) setSavedTaskCount((res.data.data || []).filter(t => !t.archived && !['brouillon', 'annulee'].includes(t.status)).length); })
      .catch(() => { if (!cancelled) setSavedTaskCount(null); });
    return () => { cancelled = true; };
  }, [selectedDate, tasksRefreshKey]);
  const [lastCreated, setLastCreated] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users/kitchen-collaborators`).then(r => setCollaborators(r.data.data || [])).catch(err => console.error('Collaborators fetch error:', err));
  }, [isAdmin]);

  useEffect(() => {
    if (checkedItems.size === 0) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [checkedItems.size]);

  const selectedPrepItems = useMemo(
    () => STATIONS.flatMap(s => (allItems[s.id] || []).filter(i => checkedItems.has(i.id)).map(i => ({ ...i, station: s.id }))),
    [allItems, checkedItems],
  );

  const clearSelection = () => setCheckedItems(new Set());

  const handleTasksSaved = (created) => {
    clearSelection();
    setLastCreated(created);
    setTasksRefreshKey(k => k + 1);
    setActiveTab('suivi');
  };

  const handleDateChange = (value) => {
    if (checkedItems.size > 0 && !window.confirm('Vous avez une sélection non enregistrée. Changer de date ?')) return;
    if (checkedItems.size > 0) clearSelection();
    setSelectedDate(value);
  };

  // ===== DATA FETCHING =====
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (isAdmin) {
        const [teamRes, datesRes, ...stationResults] = await Promise.all([
          axios.get(`${API}/teams/`),
          axios.get(`${API}/prep/daily/dates`),
          ...STATIONS.map(s => axios.get(`${API}/prep/items`, { params: { station: s.id } })),
        ]);
        setTeamMembers(teamRes.data.data || []);
        setSavedDates(datesRes.data.data || []);

        const itemsByStation = {};
        STATIONS.forEach((s, idx) => {
          itemsByStation[s.id] = stationResults[idx].data.data || [];
        });
        setAllItems(itemsByStation);

        const dailyRes = await axios.get(`${API}/prep/daily`, { params: { date: selectedDate } });
        const daily = (dailyRes.data.data || []).filter(d => d.status !== 'replaced');
        setDailyPrep(daily);

        const local = {};
        for (const station of STATIONS) {
          const stationItems = itemsByStation[station.id] || [];
          for (const item of stationItems) {
            const existing = daily.find(d => d.item_id === item.id && d.station === station.id);
            local[item.id] = existing || {
              item_id: item.id, item_name: item.name, station: station.id,
              quantity: '', unit: item.unit || '', assigned_to: [], assigned_to_names: [],
              note: '', status: 'pending', sort_order: item.sort_order,
            };
          }
        }
        setLocalPrep(local);
        // Task selection always starts empty (saved 'checked' flags are only used for printing)
        setCheckedItems(new Set());
      } else {
        const [datesRes, dailyRes, ...stationResults] = await Promise.all([
          axios.get(`${API}/prep/daily/dates`),
          axios.get(`${API}/prep/daily`, { params: { date: selectedDate } }),
          ...STATIONS.map(s => axios.get(`${API}/prep/items`, { params: { station: s.id } })),
        ]);
        setSavedDates(datesRes.data.data || []);
        const daily = (dailyRes.data.data || []).filter(d => d.status !== 'replaced');
        setDailyPrep(daily);

        const itemsByStation = {};
        STATIONS.forEach((s, idx) => {
          itemsByStation[s.id] = stationResults[idx].data.data || [];
        });
        setAllItems(itemsByStation);

        const local = {};
        for (const station of STATIONS) {
          const stationItems = itemsByStation[station.id] || [];
          for (const item of stationItems) {
            const existing = daily.find(d => d.item_id === item.id && d.station === station.id);
            local[item.id] = existing || {
              item_id: item.id, item_name: item.name, station: station.id,
              quantity: '', unit: item.unit || '', assigned_to: [], assigned_to_names: [],
              note: '', status: 'pending', sort_order: item.sort_order,
            };
          }
        }
        setLocalPrep(local);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ===== ADMIN METHODS =====
  const updateLocalItem = (itemId, field, value) => {
    setLocalPrep(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
  };

  const toggleCheck = (itemId) => {
    setCheckedItems(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };
  const handleAddItem = async () => {
    if (!newItemName.trim() || !newItemStation) return;
    try {
      setAddingItem(true);
      await axios.post(`${API}/prep/items`, { name: newItemName.trim(), station: newItemStation, unit: '' });
      toast.success('Item ajouté !');
      setNewItemName('');
      setNewItemStation('');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); } finally { setAddingItem(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await axios.delete(`${API}/prep/items/${itemId}`); toast.success('Item supprimé'); fetchData(); } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleSaveAll = async () => {
    try {
      setSavingAll(true);
      const allPrepItems = [];
      for (const station of STATIONS) {
        const stationItems = allItems[station.id] || [];
        for (const item of stationItems) {
          const isChecked = checkedItems.has(item.id) || !!localPrep[item.id]?.checked;
          allPrepItems.push({
            item_id: item.id,
            item_name: item.name,
            station: station.id,
            quantity: parseFloat(localPrep[item.id]?.quantity) || 0,
            unit: localPrep[item.id]?.unit || item.unit || '',
            assigned_to: localPrep[item.id]?.assigned_to || [],
            assigned_to_names: localPrep[item.id]?.assigned_to_names || [],
            note: localPrep[item.id]?.note || '',
            checked: isChecked,
            sort_order: item.sort_order,
          });
        }
      }
      await axios.post(`${API}/prep/daily/save-all`, {
        date: selectedDate,
        items: allPrepItems,
      });
      toast.success(`Mise en place enregistrée pour le ${formatDateFR(selectedDate)} !`);
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); } finally { setSavingAll(false); }
  };

  // ===== MEMBER METHODS =====
  const handleTaskAction = async (taskId, action) => {
    try {
      await axios.patch(`${API}/prep/daily/${taskId}`, {
        status: action,
        completed_by: user?.id,
        completed_by_name: user?.name,
      });
      toast.success(action === 'done' ? 'Tâche terminée !' : 'Tâche passée');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUndoTask = async (taskId) => {
    try {
      await axios.patch(`${API}/prep/daily/${taskId}`, { status: 'pending' });
      toast.success('Tâche remise en attente');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // ===== HELPERS =====
  const totalItems = Object.values(allItems).reduce((sum, arr) => sum + arr.length, 0);
  const filledItems = Object.values(localPrep).filter(p => p.checked).length;
  const savedCheckedIds = new Set(Object.values(localPrep).filter(p => p.checked).map(p => p.item_id));
  const printItems = checkedItems.size > 0 ? checkedItems : savedCheckedIds;

  const isMyTask = (prep) => {
    const memberId = user?.member_id || '';
    const userName = user?.name || '';
    const ids = prep.assigned_to || [];
    const names = prep.assigned_to_names || [];
    return ids.includes(memberId) || names.includes(userName);
  };

  // ================================================================
  // ===== MEMBER VIEW =====
  // ================================================================
  if (!isAdmin) {
    const myAssignedAll = Object.values(localPrep).filter(p => isMyTask(p));
    const myDoneCount = myAssignedAll.filter(p => p.status === 'done').length;
    const myPendingCount = myAssignedAll.filter(p => p.status === 'pending' || p.status === 'skipped').length;

    return (
      <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="gerer-page">
        {/* Member Header */}
        <div className="max-w-6xl mx-auto mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
                Mise en Place
              </h1>
              <p className="text-gray-500 text-sm">
                Bonjour {user?.name} ! {myPendingCount} tâche(s) à faire &bull; {formatDateShortFR(selectedDate)}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowSavedDates(true)}
              className="rounded-full bg-white border-gray-200 text-gray-700 text-xs" data-testid="saved-dates-btn">
              <Archive size={14} className="mr-1" />Historique
            </Button>
          </div>

          {/* Progress bar */}
          {myAssignedAll.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm">Mes tâches</span>
                <span className="text-neon-pink font-bold text-sm">{myDoneCount}/{myAssignedAll.length}</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-neon-pink to-neon-yellow rounded-full transition-all duration-500"
                  style={{ width: `${myAssignedAll.length > 0 ? (myDoneCount / myAssignedAll.length) * 100 : 0}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* General List - All stations */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : selectedStation === 'all' ? (
            <TileGrid testId="station-tiles" onSelect={setSelectedStation} tiles={STATIONS.map(s => {
              const items = allItems[s.id] || [];
              const mine = items.filter(i => isMyTask(localPrep[i.id] || {}) && localPrep[i.id]?.status !== 'done').length;
              return { id: s.id, testPrefix: 'station-tile', label: s.label, sub: `${items.length} items`, icon: s.icon, cfg: s, badge: mine };
            })} />
          ) : (
            <div className="space-y-4 mb-8">
              <BackButton onClick={() => setSelectedStation('all')} label="Tous les postes" testId="station-back-btn" />
              {STATIONS.filter(s => s.id === selectedStation).map(station => {
                const items = allItems[station.id] || [];
                return (
                  <div key={station.id} className={`bg-white border-2 ${station.border} rounded-3xl overflow-hidden`} data-testid={`station-section-${station.id}`}>
                    <SectionHeader icon={station.icon} cfg={station} label={station.label} sub={`${items.length} items`} />
                    <div className="divide-y divide-gray-100">
                      {items.map((item) => {
                        const prep = localPrep[item.id] || {};
                        return (
                          <MemberPrepItemRow
                            key={item.id}
                            item={item}
                            prep={prep}
                            isMine={isMyTask(prep)}
                            onTaskAction={handleTaskAction}
                            onUndoTask={handleUndoTask}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* History Dialog (member) */}
        <Dialog open={showSavedDates} onOpenChange={setShowSavedDates}>
          <DialogContent className="sm:max-w-lg bg-white border-gray-200 max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl text-gray-900 flex items-center gap-2"><Archive size={20} className="text-neon-pink" />Historique</DialogTitle></DialogHeader>
            <div className="py-4 space-y-2">
              {savedDates.length === 0 ? <p className="text-center py-6 text-gray-400">Aucun historique</p> : savedDates.map((date) => (
                <button key={date} data-testid={`saved-date-${date}`} onClick={() => { setSelectedDate(date); setShowSavedDates(false); setSelectedStation('all'); }}
                  className={`w-full bg-gray-50 border rounded-xl p-4 text-left hover:bg-gray-100 transition-colors ${date === selectedDate ? 'border-neon-pink/50 bg-neon-pink/5' : 'border-gray-100'}`}>
                  <span className="font-heading font-bold text-gray-900">{formatDateFR(date)}</span>
                  {date === today && <Badge className="ml-2 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 text-[10px]">Aujourd'hui</Badge>}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ================================================================
  // ===== ADMIN VIEW =====
  // ================================================================
  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="gerer-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
              Mise en Place
            </h1>
            <p className="text-gray-500 text-sm" data-testid="mep-header-summary">
              {savedTaskCount ? `${savedTaskCount} tâche${savedTaskCount > 1 ? 's' : ''} enregistrée${savedTaskCount > 1 ? 's' : ''}` : `${filledItems}/${totalItems} remplis`} &bull; {formatDateShortFR(selectedDate)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setTaskDialog('free')} size="sm"
              className="rounded-full h-10 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold" data-testid="new-task-btn">
              <Plus size={14} className="mr-1" />Nouvelle tâche
            </Button>
            {canManageItems && (
              <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}
                className={`rounded-full h-10 border-gray-200 text-xs ${editMode ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-700'}`}
                data-testid="admin-edit-toggle">
                <Edit3 size={14} className="mr-1" />{editMode ? 'Quitter' : 'Admin'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowSavedDates(true)}
              className="rounded-full h-10 bg-white border-gray-200 text-gray-700 text-xs" data-testid="saved-dates-btn">
              <Archive size={14} className="mr-1" />Historique
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPrintDialog(true)}
              className="rounded-full h-10 bg-white border-gray-200 text-gray-700 text-xs" data-testid="print-btn">
              <Printer size={14} className="mr-1" />Imprimer
            </Button>
          </div>
        </div>

        {/* Date selector */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4">
          <Label className="text-gray-500 text-xs">Date de la mise en place</Label>
          <input type="date" value={selectedDate} onChange={(e) => handleDateChange(e.target.value)}
            className="mt-1 block w-full h-11 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-pink/50" data-testid="prep-date-input" />
          {savedDates.includes(selectedDate) && (
            <Badge className="mt-2 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">Enregistrée</Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-12 w-full sm:w-auto bg-gray-100 rounded-xl p-1 mb-2" data-testid="gerer-tabs">
            <TabsTrigger value="liste" className="flex-1 sm:flex-none h-10 rounded-lg px-5 text-sm data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow" data-testid="tab-liste">Liste</TabsTrigger>
            <TabsTrigger value="suivi" className="flex-1 sm:flex-none h-10 rounded-lg px-5 text-sm data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow" data-testid="tab-suivi">Suivi des tâches</TabsTrigger>
          </TabsList>
          <TabsContent value="suivi" className="mt-2">
            {lastCreated.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-300 rounded-2xl px-4 py-3 mb-4 text-sm text-emerald-800 flex items-center justify-between gap-2" data-testid="last-created-banner">
                <span>{lastCreated.length} tâche{lastCreated.length > 1 ? 's' : ''} nouvellement créée{lastCreated.length > 1 ? 's' : ''} : {lastCreated.map(t => t.title).join(', ')}</span>
                <button type="button" onClick={() => setLastCreated([])} className="text-emerald-700 min-h-[32px] px-2" data-testid="last-created-dismiss"><X size={16} /></button>
              </div>
            )}
            <TaskTrackingPanel date={selectedDate} onDateChange={setSelectedDate} collaborators={collaborators} refreshKey={tasksRefreshKey} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Stations (tab Liste) */}
      {activeTab === 'liste' && (
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            {selectedStation === 'all' ? (
              <TileGrid testId="station-tiles" onSelect={setSelectedStation} tiles={STATIONS.map(s => {
                const items = allItems[s.id] || [];
                return { id: s.id, testPrefix: 'station-tile', label: s.label, sub: `${items.length} items`, icon: s.icon, cfg: s, badge: items.filter(i => checkedItems.has(i.id)).length };
              })} />
            ) : (
              <>
                <BackButton onClick={() => setSelectedStation('all')} label="Tous les postes" testId="station-back-btn" />
                {STATIONS.filter(s => s.id === selectedStation).map(station => {
                  const items = allItems[station.id] || [];
                  const selectedInStation = items.filter(i => checkedItems.has(i.id)).length;
                  return (
                    <div key={station.id} className={`bg-white border-2 ${station.border} rounded-3xl overflow-hidden`} data-testid={`station-section-${station.id}`}>
                      <SectionHeader icon={station.icon} cfg={station} label={station.label} sub={`${items.length} items`}
                        right={selectedInStation > 0 && <Badge className="bg-neon-pink text-white border-0">{selectedInStation}</Badge>} />
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => (
                          <AdminPrepItemRow
                            key={item.id}
                            item={item}
                            prep={localPrep[item.id] || {}}
                            isChecked={checkedItems.has(item.id)}
                            editMode={editMode}
                            onToggleCheck={toggleCheck}
                            onUpdateField={updateLocalItem}
                            onDelete={handleDeleteItem}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Admin: Add Item */}
            {editMode && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1">
                    <Label className="text-gray-500 text-xs">Nom du nouvel item</Label>
                    <Input value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Ex: Houmous..."
                      className="mt-1 h-10 rounded-xl bg-gray-100 border-gray-200 text-gray-900" onKeyDown={(e) => e.key === 'Enter' && handleAddItem()} data-testid="new-item-input" />
                  </div>
                  <div className="w-full sm:w-48">
                    <Label className="text-gray-500 text-xs">Poste</Label>
                    <Select value={newItemStation} onValueChange={setNewItemStation}>
                      <SelectTrigger className="mt-1 h-10 rounded-xl bg-gray-100 border-gray-200 text-gray-700" data-testid="new-item-station-select">
                        <SelectValue placeholder="Choisir poste" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {STATIONS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddItem} disabled={addingItem || !newItemName.trim() || !newItemStation}
                    className="bg-neon-pink hover:bg-neon-pink/90 rounded-xl h-10 w-full sm:w-auto" data-testid="add-item-btn">
                    {addingItem ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Legacy save (notes + coches) — hidden while a task selection is active to keep a single action bar */}
        {!loading && checkedItems.size === 0 && (
          <div className="flex justify-center sticky bottom-24 md:bottom-8 z-10">
            <Button onClick={handleSaveAll} disabled={savingAll}
              className="bg-neon-pink hover:bg-neon-pink/90 rounded-full px-8 py-3 text-lg shadow-neon-pink neon-glow-pink"
              data-testid="save-all-btn">
              {savingAll ? <Loader2 className="animate-spin mr-2" size={20} /> : <Save size={20} className="mr-2" />}
              Enregistrer la mise en place
            </Button>
          </div>
        )}

        <SelectionBar count={checkedItems.size} onConfigure={() => setTaskDialog('bulk')} onSave={() => setTaskDialog('bulk')} onClear={clearSelection} />
      </div>
      )}

      <TaskFormDialog
        open={!!taskDialog}
        onOpenChange={(v) => { if (!v) setTaskDialog(null); }}
        mode={taskDialog === 'free' ? 'free' : 'bulk'}
        items={taskDialog === 'bulk' ? selectedPrepItems : []}
        defaultDate={selectedDate}
        collaborators={collaborators}
        onSaved={handleTasksSaved}
      />

      {/* Saved Dates Dialog */}
      <Dialog open={showSavedDates} onOpenChange={setShowSavedDates}>
        <DialogContent className="sm:max-w-lg bg-white border-gray-200 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-gray-900 flex items-center gap-2"><Archive size={20} className="text-neon-pink" />Mises en place enregistrées</DialogTitle></DialogHeader>
          <div className="py-4 space-y-2">
            {savedDates.length === 0 ? <p className="text-center py-6 text-gray-400">Aucune mise en place enregistrée</p> : savedDates.map((date) => (
              <button key={date} data-testid={`saved-date-${date}`} onClick={() => { setSelectedDate(date); setShowSavedDates(false); setSelectedStation('all'); }}
                className={`w-full bg-gray-50 border rounded-xl p-4 text-left hover:bg-gray-100 transition-colors ${date === selectedDate ? 'border-neon-pink/50 bg-neon-pink/5' : 'border-gray-100'}`}>
                <span className="font-heading font-bold text-gray-900">{formatDateFR(date)}</span>
                {date === today && <Badge className="ml-2 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 text-[10px]">Aujourd'hui</Badge>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <PrintPrepDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        stations={STATIONS}
        allItems={allItems}
        localPrep={localPrep}
        selectedDate={selectedDate}
        printStation={printStation}
        setPrintStation={setPrintStation}
        checkedItems={printItems}
      />
    </div>
  );
};

export default GererPage;
